
import React, { useEffect } from "react";
import { CircleSuccessIcon, CloseIcon, CircleWarningIcon, CircleErrorIcon } from "../../theme/icons";

const Ctx = React.createContext();

const ToastContainer = props => <div className="fixed top-4 right-4 z-50 max-w-500" {...props} />;

const Toast = ({ children, onDismiss, variant }) => {
    const icons = {
        success: <CircleSuccessIcon />,
        error: <CircleErrorIcon />,
        warning: <CircleWarningIcon />,
        info:<CircleWarningIcon />
    }

    const components = {
        success: 'border-success-400 bg-success-50 mb-2',
        error: 'border-danger-400 bg-danger-50 mb-2',
        warning: 'border-warning-400 bg-orange-50 mb-2',
        info: 'border-warning-400 bg-primary-50 mb-2'
    }

    return (
        <div className={`border ${components[variant]} p-2 rounded-md z-9999`}
            id="static-example"
            role="alert"
            aria-live="assertive"
            aria-atomic="true"
            data-te-autohide="true"
            data-te-toast-init
            data-te-toast-show>
            <div className="flex items-center justify-between rounded-t-lg  bg-clip-padding text-primary-700">
                <p className="flex items-center">
                    {icons[variant]}
                    <span className="ml-2 text-sm">
                        {children}
                    </span>
                </p>
                <div className="flex items-center">
                    <button
                        onClick={onDismiss}
                        type="button"
                        className="ml-2 box-content rounded-none border-none opacity-80 hover:no-underline hover:opacity-75 focus:opacity-100 focus:shadow-none focus:outline-none"
                        data-te-toast-dismiss
                        aria-label="Close">
                        <span
                            className="w-[1em] focus:opacity-100 disabled:pointer-events-none disabled:select-none disabled:opacity-25 [&.disabled]:pointer-events-none [&.disabled]:select-none [&.disabled]:opacity-25">
                            <CloseIcon />
                        </span>
                    </button>
                </div>
            </div>
        </div>
    );
}

let toastCount = 0;
let timeout = 0;

export function ToastProvider({ children }) {
    const [toasts, setToasts] = React.useState([]);

    const setToastMessage = ({ message, variant }) => {
        const id = toastCount++;
        const toast = { message, variant, id };
        setToasts([...toasts, toast]);
    };
    const remove = id => {
        const newToasts = toasts.filter(t => t.id !== id);
        setToasts(newToasts);
    };
    // avoid creating a new fn on every render
    const onDismiss = id => () => remove(id);

    useEffect(() => {
        if (timeout) {
            clearTimeout(timeout);
        }
        timeout = setTimeout(() => {
            setToasts([]);
        }, process.env.REACT_APP_TOAST_TIMEOUT);
        return () => {
            clearTimeout(timeout);
        }
    }, [toasts?.length])

    return (
        <Ctx.Provider value={{ setToastMessage, remove }}>
            {children}
            <ToastContainer>
                {toasts.map(({ message, variant, id, ...rest }) => (
                    <Toast key={id} Toast={Toast} variant={variant} onDismiss={onDismiss(id)} {...rest}>
                        {message}
                    </Toast>
                ))}
            </ToastContainer>
        </Ctx.Provider>
    );
}

export const useToasts = () => React.useContext(Ctx);
